/**
 * PurchasesHub — Page Factures Fournisseurs unifiée avec tabs internes.
 *
 * 3 onglets pour tout ce qui concerne les factures fournisseurs :
 *   ┌─ Toutes         (liste courante avec filtres/actions)
 *   ├─ Import & OCR   (upload batch + historique jobs)
 *   └─ Historique     (archive expert-comptable, période 12m par défaut)
 *
 * Onglet actif via ?tab= pour deep-links depuis n'importe où.
 * Toutes les routes existantes restent fonctionnelles (redirects dans App.js).
 */
import { useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { FileText, Upload, Archive } from "lucide-react";
import PurchasesList from "@/pages/PurchasesList";
import ImportAchatsBatch from "@/pages/ImportAchatsBatch";
import ImportJobs from "@/pages/ImportJobs";
import HistoriqueAchats from "@/pages/HistoriqueAchats";

const TAB_ALIASES = {
  all: "all", toutes: "all",
  import: "import", imports: "import", ocr: "import",
  history: "history", historique: "history",
};

export default function PurchasesHub() {
  const [params, setParams] = useSearchParams();
  const activeParam = params.get("tab");
  const active = TAB_ALIASES[activeParam] || "all";
  const [runningJobs, setRunningJobs] = useState(0);

  useEffect(() => {
    api.get("/import-jobs", { params: { limit: 20 } }).then((r) => {
      const jobs = r.data.jobs || [];
      setRunningJobs(jobs.filter((j) => j.status === "processing" && j.kind === "achats").length);
    }).catch(() => {});
  }, []);

  const setTab = (t) => {
    const p = new URLSearchParams(params);
    if (t === "all") p.delete("tab");
    else p.set("tab", t);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="purchases-hub">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Factures fournisseurs
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Toutes tes factures reçues, imports IA et archives — en un seul endroit.
        </p>
      </div>

      <Tabs value={active} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 h-auto">
          <TabsTrigger value="all" data-testid="purchases-tab-all"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <FileText className="h-4 w-4" />
            Toutes
          </TabsTrigger>
          <TabsTrigger value="import" data-testid="purchases-tab-import"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Upload className="h-4 w-4" />
            Import & OCR
            {runningJobs > 0 && (
              <Badge className="ml-1 bg-amber-100 text-amber-700 border border-amber-200 h-5 px-1.5 text-[10px] animate-pulse">
                {runningJobs}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="purchases-tab-history"
                       className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2 px-4 py-2">
            <Archive className="h-4 w-4" />
            Historique
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <PurchasesList embedded />
        </TabsContent>
        <TabsContent value="import" className="mt-6 space-y-6">
          <ImportAchatsBatch embedded />
          <div className="pt-4 border-t border-slate-200">
            <div className="mb-3">
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Historique des imports
              </h2>
              <p className="text-xs text-slate-500">
                Les 20 derniers jobs d'import IA (ventes + achats).
              </p>
            </div>
            <ImportJobs embedded />
          </div>
        </TabsContent>
        <TabsContent value="history" className="mt-6">
          <HistoriqueAchats embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
